import { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { getVistaPreviaRecordatorioVencida, recordarCuotaVencida } from '../../services/cobranzaService';

/** Menos de esto desde el último recordatorio y el modal avisa antes de mandar otro. */
const DIAS_SIN_INSISTIR = 3;

const mensajeDeError = (e, generico) => e?.response?.data?.message || e?.response?.data?.mensaje || generico;

function diasDesde(fechaHoraISO) {
    if (!fechaHoraISO) return null;
    const fecha = new Date(fechaHoraISO);
    if (Number.isNaN(fecha.getTime())) return null;
    const hoy = new Date();
    const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const inicioFecha = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
    return Math.round((inicioHoy - inicioFecha) / 86400000);
}

function textoHace(dias) {
    if (dias === 0) return 'hoy';
    if (dias === 1) return 'ayer';
    return `hace ${dias} días`;
}

/**
 * Recordar una cuota vencida por WhatsApp (número de los avisos), desde el listado Deudores.
 *
 * Lo importante es no saturar al cliente: arriba se ve cuándo fue el último recordatorio
 * (automático o a mano) y, si fue hace menos de 3 días, se avisa y el botón dice "Enviar igual".
 * El texto sale de la plantilla (Configuración → WhatsApp) y se puede corregir antes de mandar.
 * Lo que se manda queda en la bitácora del cobro.
 */
export default function ModalRecordarCuotaVencida({ show, cobro, onCerrar, onEnviado }) {
    const [vista, setVista] = useState(null);
    const [mensaje, setMensaje] = useState('');
    const [cargando, setCargando] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');

    // Depende de `show` y del id, no del objeto de la fila: la fila cambia en cada recarga del listado.
    const idCobro = cobro?.idCobro;
    useEffect(() => {
        if (!show || !idCobro) return undefined;
        let vigente = true;
        setCargando(true);
        setError('');
        setVista(null);
        setMensaje('');
        getVistaPreviaRecordatorioVencida(idCobro)
            .then((data) => {
                if (!vigente) return;
                setVista(data);
                setMensaje(data?.mensaje || '');
            })
            .catch((e) => { if (vigente) setError(mensajeDeError(e, 'No se pudo armar el recordatorio.')); })
            .finally(() => { if (vigente) setCargando(false); });
        return () => { vigente = false; };
    }, [show, idCobro]);

    const enviar = async () => {
        setEnviando(true);
        setError('');
        try {
            const resultado = await recordarCuotaVencida(idCobro, mensaje);
            onEnviado?.(resultado);
        } catch (e) {
            setError(mensajeDeError(e, 'No se pudo mandar el recordatorio.'));
        } finally {
            setEnviando(false);
        }
    };

    const dias = diasDesde(vista?.ultimoRecordatorio);
    const reciente = dias != null && dias < DIAS_SIN_INSISTIR;
    const quien = vista?.ultimoRecordatorioAutomatico ? 'automático' : (vista?.ultimoRecordatorioUsuario || 'a mano');
    const sinTelefono = vista && !vista.telefono;
    const sinVencidas = vista && !vista.cuotasVencidas;
    const nombre = [cobro?.apellido, cobro?.nombre].filter(Boolean).join(', ');

    return (
        <Modal show={show} onHide={enviando ? undefined : onCerrar} scrollable centered>
            <Modal.Header closeButton={!enviando}>
                <Modal.Title style={{ fontSize: '1.1rem' }}>
                    <i className="bi bi-whatsapp me-2" style={{ color: '#16a34a' }} />Recordar cuota vencida
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2">
                    <strong>{nombre}</strong>
                    {vista?.telefono && <span className="text-muted"> · {vista.telefono}</span>}
                </p>
                {cargando && <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>}
                {!cargando && vista && (
                    <>
                        {vista.cuotasVencidas && (
                            <p className="small mb-2" style={{ color: '#b91c1c' }}>
                                <i className="bi bi-exclamation-circle me-1" />Vencida: {vista.cuotasVencidas}
                            </p>
                        )}
                        {vista.ultimoRecordatorio ? (
                            <div className={`alert ${reciente ? 'alert-warning' : 'alert-light border'} py-2 small`}>
                                Último recordatorio: <strong>{textoHace(dias)}</strong>
                                {' '}({new Date(vista.ultimoRecordatorio).toLocaleDateString('es-AR')}, {quien}).
                                {reciente && ' ¿Seguro que querés mandarle otro? La idea es no llenarlo de mensajes.'}
                            </div>
                        ) : (
                            <p className="small text-muted mb-2">No se le mandó ningún recordatorio todavía.</p>
                        )}
                        {sinTelefono ? (
                            <div className="alert alert-warning py-2 small mb-0">
                                El cliente no tiene un teléfono válido cargado. Cargalo en su ficha para poder avisarle.
                            </div>
                        ) : sinVencidas ? (
                            <div className="alert alert-info py-2 small mb-0">
                                El cliente ya no tiene cuotas vencidas sin pagar.
                            </div>
                        ) : (
                            <>
                                <label className="form-label small fw-semibold" htmlFor="recordatorio-mensaje">Mensaje</label>
                                <textarea
                                    id="recordatorio-mensaje"
                                    className="form-control"
                                    rows={7}
                                    maxLength={1000}
                                    value={mensaje}
                                    onChange={(e) => setMensaje(e.target.value)}
                                />
                                <div className="form-text">
                                    Sale por el número de WhatsApp de los avisos y queda en la bitácora del cobro.
                                </div>
                            </>
                        )}
                    </>
                )}
                {error && <div className="alert alert-danger py-2 small mt-3 mb-0">{error}</div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCerrar} disabled={enviando}>Cancelar</Button>
                <Button
                    variant={reciente ? 'warning' : 'success'}
                    onClick={enviar}
                    disabled={enviando || cargando || !vista || sinTelefono || sinVencidas || !mensaje.trim()}
                >
                    {enviando ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="bi bi-send me-2" />}
                    {reciente ? 'Enviar igual' : 'Enviar WhatsApp'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
